import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import ShippingMethodsList from "../shipping/ShippingMethodsList";
import ShippingSummary from "../shipping/ShippingSummary";
import CoinsRedeemBox from "../checkout/CoinsRedeemBox";
import LoadingScreen from "../../components/ui/Loading/Loading";
import Breadcrumb from "../../components/ui/Breadcrumb/Breadcrumb";
import { firstSupportedMethod, getCheckoutMethodAvailability } from "../../utils/methodAvailability";
import "./Checkout.css";

export default function Checkout() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [shippingMethod, setShippingMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [verifyBlock, setVerifyBlock] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [paymentAvailability, setPaymentAvailability] = useState(null);
  const [coinsData, setCoinsData] = useState({
    coins_to_use: 0,
    coins_discount: 0,
    final_total: 0,
    max_usable_coins: 0,
  });

  const submitLock = useRef(false);
  const messageRef = useRef(null);

  const isDigitalFlag = (d) => {
    if (d === 1 || d === "1" || d === true) return true;
    const v = String(d ?? "").trim().toLowerCase();
    return ["true", "yes", "on"].includes(v);
  };

  const getItemIsDigital = (item) => {
    const candidates = [
      item?.is_digital, item?.product_is_digital, item?.product?.is_digital,
      item?.variant?.is_digital, item?.variant?.product?.is_digital,
      item?.digital, item?.product?.digital, item?.variant?.digital,
      item?.variant?.product?.digital, item?.type === "digital" ? 1 : 0,
      item?.product?.type === "digital" ? 1 : 0,
      item?.variant?.type === "digital" ? 1 : 0,
      item?.variant?.product?.type === "digital" ? 1 : 0,
      item?.product_type === "digital" ? 1 : 0,
    ];
    return candidates.some(isDigitalFlag);
  };

  const normalizeEndpoint = (endpoint) => {
    return String(endpoint || "")
      .replace(/^https?:\/\/[^/]+/i, "")
      .replace(/^\/+/, "")
      .replace(/^api\//, "");
  };

  const getItemUnitPrice = (item) => {
    return (
      Number(
        item?.price ?? item?.sale_price ?? item?.variant?.sale_price ??
        item?.variant?.price ?? item?.product?.final_price ??
        item?.product?.sale_price ?? item?.product?.price ??
        item?.variant?.product?.final_price ?? item?.variant?.product?.sale_price ??
        item?.variant?.product?.price ?? 0
      ) || 0
    );
  };

  const getItemLineTotal = (item) => {
    const qty = Number(item?.quantity || 0);
    if (item?.line_total !== undefined && item?.line_total !== null) {
      return Number(item.line_total) || 0;
    }
    return getItemUnitPrice(item) * qty;
  };

  const total = items.reduce((sum, i) => sum + getItemLineTotal(i), 0);

  const cartType = useMemo(() => {
    if (!items || !items.length) {
      return { hasDigital: false, hasPhysical: false, isDigitalOnly: false };
    }
    let hasDigital = false;
    let hasPhysical = false;
    for (const it of items) {
      const isD = getItemIsDigital(it);
      if (isD) hasDigital = true;
      else hasPhysical = true;
    }
    return { hasDigital, hasPhysical, isDigitalOnly: hasDigital && !hasPhysical };
  }, [items]);

  const isDigitalOnly = cartType.isDigitalOnly;

  const shippingCost = useMemo(() => {
    if (!shippingMethod || isDigitalOnly) return 0;
    return Number(shippingMethod?.price || 0);
  }, [shippingMethod, isDigitalOnly]);

  const checkoutSubtotal = useMemo(() => {
    return Number((total + shippingCost).toFixed(2));
  }, [total, shippingCost]);

  const previewDiscount = Number(coinsData?.coins_discount || 0);
  const previewFinalTotal = Number(
    (coinsData?.final_total && coinsData.final_total > 0
      ? coinsData.final_total
      : checkoutSubtotal
    ).toFixed(2)
  );

  const safeApiCall = async (callback) => {
    try {
      const res = await callback();
      if (!res || typeof res !== "object") {
        return {
          status: "error",
          message: isAr ? "استجابة غير صالحة من السيرفر." : "Invalid server response.",
          body: res ?? null,
        };
      }
      if (!Object.prototype.hasOwnProperty.call(res, "status")) {
        return { ...res, status: "success", message: res.message || "" };
      }
      return res;
    } catch (err) {
      const message =
        err?.message || (isAr ? "فشل الاتصال بالسيرفر." : "Server connection failed.");
      return {
        status: "error",
        message,
        body: err?.body ?? null,
        debug: {
          http_status: err?.status ?? 0,
          body: err?.body ?? null,
          raw: err?.raw ?? null,
          error: String(err?.error || ""),
        },
      };
    }
  };

  const pushMessage = (text) => {
    setMsg(text);
    window.requestAnimationFrame(() => {
      messageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const validateCheckoutForm = () => {
    const name = (form.name || "").trim();
    const email = (form.email || "").trim();
    const phone = (form.phone || "").trim();
    const address = (form.address || "").trim();

    if (!name) return isAr ? "يرجى إدخال الاسم." : "Please enter your name.";
    if (!email) return isAr ? "يرجى إدخال البريد الإلكتروني." : "Please enter your email.";
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email))
      return isAr ? "يرجى إدخال بريد إلكتروني صالح." : "Please enter a valid email address.";
    if (!phone) return isAr ? "يرجى إدخال رقم الهاتف." : "Please enter your phone number.";
    if (!isDigitalOnly && !address)
      return isAr ? "يرجى إدخال العنوان." : "Please enter your address.";
    return "";
  };

  useEffect(() => {
    async function loadProfile() {
      const res = await safeApiCall(() => api.apiGet("me"));
      if (
        (res.status === "success" || !res.status) &&
        (res.user || res.id || res.email)
      ) {
        const user = res.user || res;
        setPaymentAvailability(user.payment_availability || null);
        setForm((prev) => ({
          ...prev,
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          address: user.address || "",
        }));
        const role = String(user.role || "").toLowerCase();
        const uid = Number(user.id || 0);
        const admin = role === "admin" || uid === 1;
        setIsAdmin(admin);
        setPaymentMethod((pm) =>
          ["test_card", "test_paypal"].includes(pm) && !admin ? "card" : pm
        );
      }
    }
    loadProfile();
  }, []);

  useEffect(() => {
    async function loadCart() {
      try {
        setLoading(true);
        setMsg("");
        setVerifyBlock(null);
        const res = await safeApiCall(() => api.apiGet("cart"));
        if (res.status === "success" || Array.isArray(res.items)) {
          setItems(res.items || []);
        } else {
          setItems([]);
          setMsg(res.message || (isAr ? "فشل تحميل السلة." : "Failed to load cart."));
        }
      } finally {
        setLoading(false);
      }
    }
    loadCart();
  }, [lang, isAr]);

  useEffect(() => {
    if (!isDigitalOnly) return;
    if (shippingMethod !== null) setShippingMethod(null);
    if (paymentMethod === "cod") setPaymentMethod("card");
    if (["test_card", "test_paypal"].includes(paymentMethod) && !isAdmin) {
      setPaymentMethod("card");
    }
  }, [isDigitalOnly, paymentMethod, shippingMethod, isAdmin]);

  const cartPaymentMethods = useMemo(() => {
    const methods = [
      { code: "cod", label_en: "Cash on delivery", label_ar: "الدفع عند الاستلام" },
      { code: "card", label_en: "Pay by card (Stripe LIVE)", label_ar: "الدفع بالبطاقة (Stripe LIVE)" },
      { code: "test_card", label_en: "Test card (Stripe Test) - Admin", label_ar: "دفع تجريبي (Stripe Test) - Admin" },
      { code: "paypal", label_en: "Pay with PayPal LIVE", label_ar: "الدفع عبر PayPal LIVE" },
      { code: "test_paypal", label_en: "Test PayPal (Sandbox) - Admin", label_ar: "دفع تجريبي (PayPal Sandbox) - Admin" },
    ].map((method) => ({
      ...method,
      availability: {
        supported:
          getCheckoutMethodAvailability(
            { payment_availability: paymentAvailability },
            "cart",
            method.code
          )?.supported !== false,
        reason:
          getCheckoutMethodAvailability(
            { payment_availability: paymentAvailability },
            "cart",
            method.code
          )?.reason || "",
      },
    }));

    return methods.filter((method) => {
      if (method.code === "cod" && isDigitalOnly) return false;
      if (["test_card", "test_paypal"].includes(method.code) && !isAdmin) return false;
      if (method.availability?.supported === false) return false;
      return true;
    });
  }, [isAdmin, isDigitalOnly, paymentAvailability]);

  const hasAvailablePaymentMethods = cartPaymentMethods.length > 0;
  const unavailablePaymentReason = useMemo(() => {
    if (hasAvailablePaymentMethods) return "";
    const methodReasons = [
      getCheckoutMethodAvailability({ payment_availability: paymentAvailability }, "cart", "card")?.reason,
      getCheckoutMethodAvailability({ payment_availability: paymentAvailability }, "cart", "paypal")?.reason,
      isAdmin
        ? getCheckoutMethodAvailability({ payment_availability: paymentAvailability }, "cart", "test_card")?.reason
        : null,
      isAdmin
        ? getCheckoutMethodAvailability({ payment_availability: paymentAvailability }, "cart", "test_paypal")?.reason
        : null,
    ].filter(Boolean);
    if (methodReasons.length > 0) {
      return methodReasons[0];
    }
    return isAr ? "لا توجد طرق دفع متاحة حاليًا." : "No payment methods are currently available.";
  }, [hasAvailablePaymentMethods, isAr, isAdmin, paymentAvailability]);

  useEffect(() => {
    if (cartPaymentMethods.some((method) => method.code === paymentMethod)) {
      return;
    }
    const nextMethod = firstSupportedMethod(cartPaymentMethods, "");
    if (nextMethod && nextMethod !== paymentMethod) {
      setPaymentMethod(nextMethod);
    }
  }, [cartPaymentMethods, paymentMethod]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const resendVerification = async () => {
    if (resendLoading) return;
    const endpoint = verifyBlock?.resendEndpoint || "/api/auth/resend_verification.php";
    const normalized = normalizeEndpoint(endpoint);
    const email = (verifyBlock?.email || form.email || "").trim();

    if (!email) {
      setVerifyBlock((p) => ({
        ...(p || {}),
        sent: false,
        error: isAr ? "البريد الإلكتروني غير موجود." : "Email is missing.",
      }));
      return;
    }

    setResendLoading(true);
    setMsg("");
    try {
      const res = await safeApiCall(() =>
        api.apiGet(`${normalized}?email=${encodeURIComponent(email)}`)
      );
      if (res.status === "success") {
        setVerifyBlock((p) => ({ ...(p || {}), sent: true, error: null }));
        setMsg(
          res.message ||
            (isAr ? "تمت إعادة إرسال رسالة التفعيل." : "Activation email resent.")
        );
      } else {
        setVerifyBlock((p) => ({
          ...(p || {}),
          sent: false,
          error:
            res.message ||
            (isAr ? "فشل إعادة إرسال رسالة التفعيل." : "Failed to resend activation email."),
        }));
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;

    setMsg("");
    setVerifyBlock(null);

    if (!items.length) {
      pushMessage(isAr ? "السلة فارغة." : "Cart is empty.");
      submitLock.current = false;
      return;
    }

    if (!hasAvailablePaymentMethods) {
      pushMessage(unavailablePaymentReason);
      submitLock.current = false;
      return;
    }

    const formError = validateCheckoutForm();
    if (formError) {
      pushMessage(formError);
      submitLock.current = false;
      return;
    }

    if (["test_card", "test_paypal"].includes(paymentMethod) && !isAdmin) {
      pushMessage(
        isAr ? "أوضاع الدفع التجريبية متاحة للأدمن فقط." : "Test payment modes are admin-only."
      );
      submitLock.current = false;
      return;
    }

    if (!isDigitalOnly && !shippingMethod?.id) {
      pushMessage(isAr ? "اختر طريقة الشحن." : "Please select a shipping method.");
      submitLock.current = false;
      return;
    }

    if (isDigitalOnly && paymentMethod === "cod") {
      pushMessage(
        isAr
          ? "الدفع عند الاستلام غير متاح للمنتجات الرقمية."
          : "Cash on Delivery is not available for digital products."
      );
      submitLock.current = false;
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        payment_method: paymentMethod,
        shipping_address: isDigitalOnly ? null : (form.address || "").trim(),
        shipping_method_id: isDigitalOnly
          ? null
          : shippingMethod?.id
            ? Number(shippingMethod.id)
            : null,
        coins_to_use: Number(coinsData?.coins_to_use || 0),
      };

      const res = await safeApiCall(() => api.apiPost("checkout", payload));

      const notVerified =
        res?.code === "ACCOUNT_NOT_VERIFIED" ||
        res?.body?.code === "ACCOUNT_NOT_VERIFIED";

      if (notVerified) {
        const r = res?.body || res;
        const endpoint =
          r?.action?.resend_verification_endpoint ||
          "/api/auth/resend_verification.php";
        const emailFromRes =
          (typeof r?.email === "string" ? r.email : null) ||
          (typeof r?.email_address === "string" ? r.email_address : null) ||
          form.email ||
          "";

        setVerifyBlock({
          message:
            r?.message ||
            (isAr
              ? "حسابك غير مفعّل. تم إرسال رسالة تفعيل إلى بريدك."
              : "Your account is not verified. We sent you a verification email."),
          resendEndpoint: endpoint,
          email: emailFromRes,
          sent: r?.email?.activation_sent ?? null,
          error: r?.email?.activation_error ?? null,
        });

        setMsg(
          r?.message ||
            (isAr
              ? "لا يمكن إنشاء الطلب قبل تفعيل الحساب."
              : "You must verify your account before placing an order.")
        );

        if (r?.email?.activation_error) {
          setMsg((current) =>
            current
              ? `${current}\n${r.email.activation_error}`
              : String(r.email.activation_error)
          );
        }
        return;
      }

      if (res.status === "error") {
        pushMessage(res.message || (isAr ? "فشل إتمام الطلب." : "Checkout failed."));
        return;
      }

      if (paymentMethod === "cod") {
        if (!res.order_id) {
          setMsg(
            isAr
              ? "لم يتم استلام رقم الطلب من السيرفر."
              : "Order ID was not returned from the server."
          );
          return;
        }
        setMsg(
          isAr
            ? `تم إنشاء الطلب رقم ${res.order_id}`
            : `Order created. ID: ${res.order_id}`
        );
        localStorage.removeItem("cart");
        window.dispatchEvent(new Event("cart-updated"));
        window.dispatchEvent(new Event("mg:notifications-refresh"));
        navigate(`/order/${res.order_id}`);
        return;
      }

      if (res.checkout_url) {
        window.dispatchEvent(new Event("mg:notifications-refresh"));
        window.location.href = res.checkout_url;
        return;
      }

      setMsg(
        isAr
          ? "تعذر بدء جلسة الدفع. لم يتم إنشاء الطلب بعد."
          : "Unable to start checkout session. No order has been created yet."
      );
    } catch (error) {
      pushMessage(
        isAr
          ? "حدث خطأ غير متوقع أثناء بدء الدفع."
          : "An unexpected error occurred while starting checkout."
      );
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const breadcrumbItems = [
    { label: isAr ? "المتجر" : "Shop", link: "/shop" },
    { label: isAr ? "السلة" : "Cart", link: "/cart" },
    { label: isAr ? "إتمام الطلب" : "Checkout", link: "/checkout" },
  ];

  if (loading) {
    return (
      <div className="checkout-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container py-5">
          <LoadingScreen lang={lang} />
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page" dir={isAr ? "rtl" : "ltr"}>
      <Breadcrumb items={breadcrumbItems} className="breadcrumb-glass" />

      <div className="container py-4 py-md-5">
        <div className="checkout-page__header">
          <h1 className="checkout-page__title">
            {isAr ? "إتمام الطلب" : "Checkout"}
          </h1>
          <Link className="btn btn-outline-secondary rounded-pill" to="/cart">
            {isAr ? "رجوع للسلة" : "Back to cart"}
          </Link>
        </div>

        {isDigitalOnly && (
          <div className="checkout-alert checkout-alert--info">
            {isAr
              ? "هذا الطلب يحتوي منتجات رقمية فقط.\nلن يتم إنشاء شحن لهذا الطلب.\nبعد الدفع سيتم إنشاء الطلب وإرسال روابط التحميل إذا لزم."
              : "This order contains digital products only.\nNo shipping is needed for this order.\nAfter payment, the order will be created and download links will be sent if applicable."}
          </div>
        )}

        {verifyBlock && (
          <div className="checkout-alert checkout-alert--warning">
            <div className="checkout-alert__title">
              {isAr ? "حسابك غير مُفعّل" : "Account not verified"}
            </div>
            <div className="checkout-alert__text">{verifyBlock.message}</div>
            <div className="checkout-alert__email">
              {isAr
                ? `البريد: ${verifyBlock.email || form.email || "-"}`
                : `Email: ${verifyBlock.email || form.email || "-"}`}
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill"
              onClick={resendVerification}
              disabled={resendLoading}
            >
              {resendLoading
                ? isAr ? "جارٍ الإرسال..." : "Sending..."
                : isAr
                  ? "إعادة إرسال رسالة التفعيل"
                  : "Resend activation email"}
            </button>
            {verifyBlock.sent === true && (
              <div className="checkout-alert__success">
                {isAr ? "✅ تم إرسال رسالة التفعيل." : "✅ Activation email sent."}
              </div>
            )}
            {verifyBlock.error && (
              <div className="checkout-alert__error">{verifyBlock.error}</div>
            )}
          </div>
        )}

        {msg && (
          <div ref={messageRef} className="checkout-alert checkout-alert--info">
            {msg}
          </div>
        )}

        <div className="row g-4">
          <div className="col-lg-7">
            <div className="checkout-panel">
              <form onSubmit={handleSubmit} noValidate>
                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "الاسم" : "Name"}</label>
                  <input
                    className="checkout-form-input"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "البريد الإلكتروني" : "Email"}</label>
                  <input
                    className="checkout-form-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "الهاتف" : "Phone"}</label>
                  <input
                    className="checkout-form-input"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                {!isDigitalOnly && (
                  <>
                    <div className="checkout-form-group">
                      <label className="checkout-form-label">{isAr ? "العنوان" : "Address"}</label>
                      <textarea
                        className="checkout-form-input checkout-form-input--textarea"
                        name="address"
                        rows={3}
                        value={form.address}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="checkout-form-group">
                      <label className="checkout-form-label">{isAr ? "طريقة الشحن" : "Shipping method"}</label>
                      <ShippingMethodsList
                        value={shippingMethod}
                        onChange={setShippingMethod}
                      />
                    </div>
                  </>
                )}

                <CoinsRedeemBox
                  subtotal={checkoutSubtotal}
                  onCoinsChange={setCoinsData}
                  disabled={submitting}
                  isAr={isAr}
                />

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "طريقة الدفع" : "Payment method"}</label>

                  <div className="payment-methods">
                    {cartPaymentMethods.map((method) => (
                      <button
                        key={method.code}
                        type="button"
                        className={`payment-method-btn ${paymentMethod === method.code ? "payment-method-btn--active" : ""}`}
                        onClick={() => setPaymentMethod(method.code)}
                      >
                        <span className="payment-method-btn__radio" />
                        <span className="payment-method-btn__label">
                          {isAr ? method.label_ar : method.label_en}
                        </span>
                      </button>
                    ))}
                  </div>

                  {isDigitalOnly && (
                    <div className="checkout-form-hint">
                      {isAr
                        ? "ملاحظة: للمنتجات الرقمية لن يظهر خيار الدفع عند الاستلام."
                        : "Note: Cash on delivery is not available for digital products."}
                    </div>
                  )}

                  {!hasAvailablePaymentMethods && (
                    <div className="checkout-alert checkout-alert--warning mt-3 mb-0">
                      {unavailablePaymentReason}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-glow w-100 rounded-pill py-2 fw-semibold mt-3"
                  disabled={submitting || resendLoading || !hasAvailablePaymentMethods}
                >
                  {submitting
                    ? isAr ? "جارٍ المعالجة..." : "Processing..."
                    : isAr
                      ? paymentMethod === "cod"
                        ? "تأكيد الطلب"
                        : "المتابعة إلى الدفع"
                      : paymentMethod === "cod"
                        ? "Place Order"
                        : "Continue to Payment"}
                </button>

                {verifyBlock && (
                  <div className="checkout-form-hint">
                    {isAr
                      ? "ملاحظة: لا يمكن إنشاء الطلب قبل تفعيل الحساب."
                      : "Note: You must verify your account before placing an order."}
                  </div>
                )}

                {paymentMethod !== "cod" && (
                  <div className="checkout-form-hint">
                    {isAr
                      ? paymentMethod === "paypal" || paymentMethod === "test_paypal"
                        ? "لن يتم إنشاء الطلب إلا بعد نجاح عملية الدفع عبر PayPal."
                        : "لن يتم إنشاء الطلب إلا بعد نجاح عملية الدفع."
                      : paymentMethod === "paypal" || paymentMethod === "test_paypal"
                        ? "The order will only be created after successful PayPal payment."
                        : "The order will only be created after successful payment."}
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="checkout-summary">
              <h5 className="checkout-summary__title">{isAr ? "ملخص الطلب" : "Order Summary"}</h5>

              <ShippingSummary
                subtotal={total}
                shippingMethod={isDigitalOnly ? null : shippingMethod}
              />

              <div className="checkout-summary__divider" />

              <div className="checkout-summary__row">
                <span>{isAr ? "الإجمالي قبل الخصم" : "Subtotal before discount"}</span>
                <strong>${checkoutSubtotal.toFixed(2)}</strong>
              </div>

              <div className="checkout-summary__row checkout-summary__row--discount">
                <span>{isAr ? "خصم النقاط" : "Coins discount"}</span>
                <strong>- ${previewDiscount.toFixed(2)}</strong>
              </div>

              <div className="checkout-summary__row checkout-summary__row--total">
                <span>{isAr ? "الإجمالي النهائي" : "Final total"}</span>
                <strong className="checkout-summary__total">${previewFinalTotal.toFixed(2)}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}